import { useEffect, useRef, useState } from "react";

interface UseStickToBottomScrollResult {
  scrollRef: React.RefObject<HTMLDivElement>;
  isAtBottom: boolean;
  hasOverflow: boolean;
  onScroll: () => void;
  scrollToBottom: () => void;
}

// 채팅 스크롤 컨테이너의 "하단 고정" 동작을 캡슐화한다.
// - deps 변경 (turns / isStreaming) 시 사용자가 하단에 있으면 자동 하단 유지
// - paint 후 raf 한 frame 안전망으로 정확한 scrollHeight 기준 재정렬
// - 자식(버블) size 변경을 ResizeObserver 로 추적해 floating 버튼 표시 갱신
export function useStickToBottomScroll(
  deps: ReadonlyArray<unknown>
): UseStickToBottomScrollResult {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasOverflow, setHasOverflow] = useState(false);

  const measureChatScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    const atBottom = distanceFromBottom <= 40;
    stickToBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
    setHasOverflow(el.scrollHeight > el.clientHeight + 1);
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // mount + deps 변경 통합 effect. 즉시 한 번 정렬/측정한 뒤, paint 가 끝난
  // 다음 frame 에서 raf 안전망으로 scrollHeight 가 정확해진 시점에 한 번 더
  // 정렬한다. 화면 재진입 시에도 동일 흐름으로 처리되어 별도 mount effect 불필요.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    measureChatScroll();
    const raf = requestAnimationFrame(() => {
      const node = scrollRef.current;
      if (!node) return;
      if (stickToBottomRef.current) {
        node.scrollTop = node.scrollHeight;
      }
      measureChatScroll();
    });
    return () => cancelAnimationFrame(raf);
  }, deps);

  // 자식(버블) size 변경 추적 — 접힘/펼침으로 overflow 가 변하면 floating
  // 버튼 표시도 같이 갱신. 직접 자식만 observe (재귀 불필요).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => measureChatScroll());
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    schedule();
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, deps);

  return {
    scrollRef,
    isAtBottom,
    hasOverflow,
    onScroll: measureChatScroll,
    scrollToBottom,
  };
}
